/* Language prompt (EN/DA).
   Shows a dismissible dialog when the browser language differs from the
   site language, but only once Shopify's cookie banner has been resolved
   (shouldShowBanner() === false or visitorConsentCollected). Never redirects:
   the visitor submits Shopify's localization form to switch language. */
(function () {
  'use strict';

  var COOKIE = 'so_lang_choice';
  var SESSION = 'so_lang_choice';
  var SUPPORTED = ['en', 'da'];
  var BANNER_ID = 'shopify-pc__banner';
  var OPEN_CLASS = 'so-lang-prompt-open';
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])';

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, days) {
    var cookie = name + '=' + encodeURIComponent(value) + '; Max-Age=' + days * 86400 + '; Path=/; SameSite=Lax';
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function readSession() {
    try { return sessionStorage.getItem(SESSION); } catch (e) { return null; }
  }

  function writeSession(value) {
    try { sessionStorage.setItem(SESSION, value); } catch (e) { /* private mode */ }
  }

  function hasChoice() {
    return Boolean(readCookie(COOKIE) || readSession());
  }

  function normalize(tag) {
    return String(tag || '').slice(0, 2).toLowerCase();
  }

  function browserLanguage() {
    var list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (var i = 0; i < list.length; i++) {
      var code = normalize(list[i]);
      if (SUPPORTED.indexOf(code) !== -1) return code;
    }
    return null;
  }

  function hasBrowserLanguages() {
    return Boolean((navigator.languages && navigator.languages.length) || navigator.language);
  }

  /* Only used when the browser exposes no language at all; Shopify derives its
     suggestion from the same Accept-Language header, so asking it when we already
     know the browser language would be a wasted request. */
  function fetchSuggestedLanguage(root, currentLang) {
    var base = root.replace(/\/$/, '');
    var url = base + '/browsing_context_suggestions.json?language[enabled]=true&language[exclude]=' + encodeURIComponent(currentLang);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var first = data && data.suggestions && data.suggestions[0];
        var handle = first && first.parts && first.parts.language && first.parts.language.handle;
        var code = normalize(handle);
        return SUPPORTED.indexOf(code) !== -1 ? code : null;
      })
      .catch(function () { return null; });
  }

  function painted(el) {
    if (!el || !el.isConnected) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity) === 0) return false;
    var rect = el.getBoundingClientRect();
    return rect.width >= 8 && rect.height >= 8;
  }

  function bannerVisible() {
    return painted(document.getElementById(BANNER_ID));
  }

  /* True when Shopify's Customer Privacy API says the banner must not show.
     Leftover #shopify-pc__banner nodes after Decline all must not block us. */
  function consentResolvedByApi() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.shouldShowBanner !== 'function') return false;
    try {
      return privacy.shouldShowBanner() === false;
    } catch (e) {
      return false;
    }
  }

  function consentPending() {
    if (consentResolvedByApi()) return false;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.shouldShowBanner === 'function') {
      try {
        if (privacy.shouldShowBanner()) return true;
      } catch (e) { /* treat as unresolved, fall through to DOM */ }
    }
    return bannerVisible();
  }

  function waitForPrivacyApi(callback) {
    var tries = 0;
    (function poll() {
      if ((window.Shopify && window.Shopify.customerPrivacy) || tries++ >= 12) return callback();
      setTimeout(poll, 250);
    })();
  }

  function setup(root) {
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    var card = root.querySelector('.so-lang-prompt__card');
    var backdrop = root.querySelector('[data-lang-prompt-backdrop]');
    var closeBtn = root.querySelector('[data-lang-prompt-close]');
    var titleEl = root.querySelector('[data-lang-prompt-title]');
    var textEl = root.querySelector('[data-lang-prompt-text]');
    var actions = root.querySelector('.so-lang-prompt__actions');
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-lang-prompt-choice]'));

    var currentLang = normalize(root.dataset.lang);
    var showMode = root.dataset.showMode || 'mismatch';
    var delay = parseInt(root.dataset.delay, 10) || 0;
    var rememberDays = parseInt(root.dataset.rememberDays, 10) || 365;
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);

    var isOpen = false;
    var previousFocus = null;
    var bannerObserver = null;
    var homeSection = root.parentNode;

    function applySuggestion(lang) {
      var target = lang || currentLang;
      if (root.dataset['title' + capitalize(target)]) titleEl.textContent = root.dataset['title' + capitalize(target)];
      if (root.dataset['text' + capitalize(target)]) textEl.textContent = root.dataset['text' + capitalize(target)];
      card.setAttribute('lang', target);

      buttons.forEach(function (btn) {
        var isPrimary = btn.dataset.langPromptChoice === target;
        btn.classList.toggle('so-lang-prompt__btn--primary', isPrimary);
        if (isPrimary && actions.firstElementChild !== btn.form) actions.insertBefore(btn.form, actions.firstElementChild);
      });
    }

    function capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function focusables() {
      return Array.prototype.filter.call(card.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
    }

    function onKeydown(event) {
      root.removeAttribute('data-initial-focus');
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !card.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !card.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    /* PageContainer is position:relative (and may gain a transform from the
       drawer). Pinning the dialog to <body> keeps the card in the viewport
       instead of centering it on the full page height below the fold. */
    function mountToViewport() {
      if (!document.body) return;
      if (root.parentNode !== document.body) document.body.appendChild(root);
    }

    function restoreHome() {
      if (homeSection && homeSection.isConnected && root.parentNode !== homeSection) {
        homeSection.appendChild(root);
      }
    }

    /* If Shopify injects its cookie banner while we are open, step aside and
       come back after consent so two dialogs never stack. Leftover banner
       nodes after Decline all must not close us. */
    function watchForBanner() {
      if (bannerObserver || !document.body) return;
      bannerObserver = new MutationObserver(function () {
        if (!isOpen || consentResolvedByApi() || !bannerVisible()) return;
        close(false);
        document.addEventListener('visitorConsentCollected', function () { open(); }, { once: true });
      });
      bannerObserver.observe(document.body, { childList: true });
    }

    function open() {
      if (isOpen) return;
      if (consentPending()) return;
      isOpen = true;
      previousFocus = document.activeElement;
      mountToViewport();
      root.removeAttribute('hidden');
      // Force a reflow so the entrance transition runs
      root.offsetHeight;
      root.setAttribute('data-open', '');
      root.setAttribute('data-initial-focus', '');
      document.documentElement.classList.add(OPEN_CLASS);
      document.addEventListener('keydown', onKeydown, true);
      watchForBanner();

      var primary = root.querySelector('.so-lang-prompt__btn--primary') || buttons[0] || card;
      window.requestAnimationFrame(function () { primary.focus({ preventScroll: true }); });
    }

    function close(restoreFocus) {
      if (!isOpen) return;
      isOpen = false;
      root.removeAttribute('data-open');
      document.documentElement.classList.remove(OPEN_CLASS);
      document.removeEventListener('keydown', onKeydown, true);

      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var finalize = function () {
        if (isOpen) return;
        root.setAttribute('hidden', '');
        restoreHome();
      };
      if (reduce) finalize();
      else setTimeout(finalize, 260);

      if (restoreFocus !== false) {
        var target = previousFocus && previousFocus.isConnected && previousFocus !== document.body ? previousFocus : null;
        if (target) target.focus({ preventScroll: true });
        else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      }
      previousFocus = null;
    }

    function remember(lang) {
      if (designMode) return;
      writeCookie(COOKIE, lang, rememberDays);
      writeSession(lang);
    }

    /* Dismissing means "stay on the current language". */
    function dismiss() {
      remember(currentLang);
      close(true);
    }

    closeBtn.addEventListener('click', dismiss);
    backdrop.addEventListener('click', dismiss);

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        var chosen = btn.dataset.langPromptChoice;
        remember(chosen);
        if (chosen === currentLang || designMode) {
          event.preventDefault();
          close(true);
          return;
        }
        btn.setAttribute('aria-busy', 'true');
        /* Let the localization form POST; Shopify redirects back to this path in the chosen language. */
      });
    });

    function showWhenConsentResolved() {
      var didSchedule = false;
      function tryOpen(extraDelay) {
        if (didSchedule || isOpen) return;
        if (consentPending()) return;
        didSchedule = true;
        setTimeout(function () {
          if (isOpen) return;
          if (consentPending()) {
            didSchedule = false;
            return;
          }
          open();
        }, extraDelay || 0);
      }

      tryOpen(0);
      document.addEventListener('visitorConsentCollected', function () { tryOpen(300); }, { once: true });

      var tries = 0;
      (function pollBanner() {
        if (didSchedule) return;
        tryOpen(0);
        if (!didSchedule && tries++ < 48) setTimeout(pollBanner, 250);
      })();

      if (document.body) {
        var hideObs = new MutationObserver(function () { tryOpen(300); });
        hideObs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'style', 'class']
        });
        setTimeout(function () { hideObs.disconnect(); }, 16000);
      }
    }

    function decide(suggested) {
      var mismatch = suggested && suggested !== currentLang;
      if (showMode !== 'always' && !mismatch) return;
      if (!suggested) {
        suggested = SUPPORTED.filter(function (code) { return code !== currentLang; })[0] || currentLang;
      }
      applySuggestion(suggested);
      waitForPrivacyApi(showWhenConsentResolved);
    }

    function start() {
      if (hasChoice()) return;
      var suggested = browserLanguage();
      if (suggested || hasBrowserLanguages()) {
        decide(suggested);
        return;
      }
      fetchSuggestedLanguage(root.dataset.root || '/', currentLang).then(decide);
    }

    applySuggestion(currentLang);

    if (designMode) {
      document.addEventListener('shopify:section:select', function (event) {
        if (event.detail && event.detail.sectionId === root.dataset.sectionId) {
          applySuggestion(browserLanguage() || currentLang);
          open();
        }
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (event.detail && event.detail.sectionId === root.dataset.sectionId) close(false);
      });
      return;
    }

    setTimeout(start, delay);
  }

  function init() {
    var root = document.querySelector('[data-language-prompt]');
    if (root) setup(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-language-prompt]');
    if (root) setup(root);
  });
})();
